import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { usePermissions } from '../../context/PermissionsContext';
import { useAuth } from '../../context/AuthContext';

const sidebarMenuItems = [
  { id: "dashboard", path: "/admin/dashboard", text: "Dashboard", icon: "fluent:grid-24-regular", permission: "dashboard_management_view" },
  { id: "livesessions", path: "/admin/livesessions", text: "LiveSessions", icon: "hugeicons:live-streaming-03", permission: "livesession_management_view" },
  // { id: "trainees",         path: "/admin/trainees",         text: "Trainees",          icon: "fluent:people-24-regular",             permission: "trainee_management_view" },
  { id: "roles-and-access", path: "/admin/roles-and-access", text: "Access Management", icon: "fluent:key-24-regular", permission: "roles_and_access_user_view" },
  { id: "billing", path: "/admin/billing", text: "Billing", icon: "solar:bill-list-linear", permission: "billing_management_view" },
  { id: "announcements", path: "/admin/announcement", text: "Announcements", icon: "fluent:megaphone-loud-24-regular", permission: "announcement_management_view" },
];

const contentSubItems = [
  { id: "blogs", path: "/admin/content/blogs", text: "Blogs", icon: "fluent:news-24-regular", permission: "content_management_blogs_view" },
  { id: "categories", path: "/admin/content/categories", text: "Blog Categories", icon: "fluent:tag-multiple-24-regular", permission: "content_management_blog_categories_view" },
  { id: "pages", path: "/admin/content/pages", text: "Pages", icon: "fluent:document-multiple-24-regular", permission: "content_management_pages_view" },
];

const courseManagementSubItems = [
  { id: "courses", path: "/admin/courses", text: "Course Console", icon: "fluent:book-24-regular", permission: "course_management_view" },
  { id: "categories", path: "/admin/categories", text: "Course Categories", icon: "fluent:tag-24-regular", permission: "course_management_categories_view" },
  { id: "admin-console", path: "/admin/admin-console", text: "Request Console", icon: "fluent:person-board-24-regular", permission: "course_approval_workflow_view" },
  { id: "certificates", path: "/admin/certificates", text: "Certificates", icon: "fluent:certificate-24-regular", permission: "certificate_management_view" },
];

const supportSubItems = [
  { id: "leads", path: "/admin/support/leads", text: "Contact Leads", icon: "fluent:person-chat-24-regular", permission: "support_contact_leads_view" },
  { id: "tickets", path: "/admin/support/tickets", text: "Support Tickets", icon: "fluent:ticket-24-regular", permission: "support_tickets_view" },
  { id: "support-faq", path: "/admin/support/faq", text: "FAQ", icon: "fluent:question-24-regular", permission: "support_faq_view" },
];

function SideBar({ open, onClose, collapsed, onCollapse, onExpand }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const { logout } = useAuth();
  const [search, setSearch] = useState('');
  const [hovered, setHovered] = useState(false);

  const isContentActive = location.pathname.startsWith('/admin/content');
  const [contentOpen, setContentOpen] = useState(isContentActive);

  const isCourseManagementActive = location.pathname.startsWith('/admin/courses') ||
    location.pathname.startsWith('/admin/categories') ||
    location.pathname.startsWith('/admin/admin-console') ||
    location.pathname.startsWith('/admin/certificates');
  const [courseOpen, setCourseOpen] = useState(isCourseManagementActive);

  const isSupportActive = location.pathname.startsWith('/admin/support');
  const [supportOpen, setSupportOpen] = useState(isSupportActive);

  // Close mobile drawer on route change
  useEffect(() => { if (onClose) onClose(); }, [location.pathname]);

  // When sidebar is expanded (not collapsed), hovered state is irrelevant
  const isFloating = collapsed && hovered;
  const isExpanded = !collapsed || isFloating;

  const isActive = (path) => {
    if (path === '/admin/courses') return location.pathname.startsWith('/admin/courses') || location.pathname.includes('/analytics/') || location.pathname.includes('edit-create');
    if (path === '/admin/livesessions') return location.pathname.startsWith('/admin/livesessions') || location.pathname.includes('livesession-dashboard') || location.pathname.includes('/trainees/');
    if (path === '/admin/roles-and-access') return location.pathname.startsWith('/admin/roles-and-access') || location.pathname.startsWith('/admin/trainees') || location.pathname.includes('traineesAnalytics');
    return location.pathname === path;
  };

  const handleLogout = () => logout();

  const filteredItems = sidebarMenuItems.filter(item =>
    item.text.toLowerCase().includes(search.toLowerCase())
  );

  // Arrow button behavior:
  // - If fully expanded (not collapsed): collapse it
  // - If floating (collapsed + hovered): just close the hover float, stay collapsed
  const handleArrowClick = () => {
    if (isFloating) {
      onExpand();        // pin it open (collapsed=false), stay expanded
      setHovered(false);
    } else {
      onCollapse();      // collapse to icon-only
    }
  };

  const navContent = (expanded) => (
    <div className="flex flex-col h-full bg-background relative">

      {/* Logo + Brand */}
      <div className={`flex items-center pt-2 pb-4 ${expanded ? 'justify-between px-5' : 'justify-center px-3'}`}>
        {expanded ? (
          <Link to="/admin/dashboard" className="flex items-center">
            <img src="../../../../logos/gomedulla_icon.png" alt="GoMedulla Logo" width={40} height={40} />
            <span className="font-logo font-bold text-2xl text-font-primary tracking-tight">GoMedulla</span>
          </Link>
        ) : (
          <Link to="/admin/dashboard">
            <img src="../../../../logos/gomedulla_icon.png" alt="GoMedulla Logo" width={40} height={40} />
          </Link>
        )}

        {expanded && <button
          onClick={handleArrowClick}
          className="w-8 h-8 absolute right-0 top-3 flex items-center justify-center rounded-l-lg border-r-4 border-stroke border  bg-white hover:bg-font-primary hover:text-white transition-colors "
        >
          <Icon icon="fluent:arrow-left-24-regular" width="16" className={`transition-transform duration-200 ${collapsed ? 'rotate-180 ' : 'rotate-0 '}`} />
        </button>}
      </div>

      {/* Search */}
      {/* {expanded && (
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2.5">
            <Icon icon="fluent:search-24-regular" width="16" className="text-gray-400 shrink-0" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search"
              className="flex-1 text-sm bg-transparent outline-none text-gray-700 placeholder-gray-400"
            />
          </div>
        </div>
      )} */}

      {/* Nav */}
      <nav className={`flex-1 overflow-y-auto pb-4 pt-5 space-y-0.5 ${expanded ? 'px-3' : 'px-2'}`}>
        {filteredItems.map((item) =>
          permissions[item.permission] && (
            <Link
              key={item.id}
              to={item.path}
              title={!expanded ? item.text : undefined}
              className={`flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-150 ${expanded ? 'px-3 py-2.5' : 'px-0 py-2.5 justify-center'
                } ${isActive(item.path)
                  ? 'bg-surface text-font-primary shadow-sm'
                  : 'text-font-secondary hover:bg-white hover:shadow-sm'
                }`}
            >
              <Icon
                icon={item.icon}
                width="30" height="30"
                className={`shrink-0 ${isActive(item.path) ? 'text-white bg-primary p-1.5 rounded-lg' : 'p-1.5 text-font-secondary'}`}
              />
              {expanded && item.text}
            </Link>
          )
        )}

        {/* Course Management Dropdown */}
        {(!search || 'course management'.includes(search.toLowerCase())) && (
          <div>
            <button
              onClick={() => expanded && setCourseOpen(!courseOpen)}
              title={!expanded ? 'Course Management' : undefined}
              className={`w-full flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-150 ${expanded ? 'px-3 py-2.5' : 'px-0 py-2.5 justify-center'
                } ${isCourseManagementActive
                  ? 'bg-surface text-font-primary shadow-sm'
                  : 'text-font-secondary hover:bg-white hover:shadow-sm'
                }`}
            >
              <Icon icon="fluent:person-board-24-regular" width="30" height="30" className={`shrink-0 ${isCourseManagementActive ? 'text-white bg-primary p-1.5 rounded-lg' : 'p-1.5 text-font-secondary'}`} />
              {expanded && (
                <>
                  <span className="flex-1 text-left">Courses</span>
                  <Icon
                    icon={courseOpen || isCourseManagementActive ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                    width="16"
                    className={isCourseManagementActive ? 'text-white/70' : 'text-font-secondary'}
                  />
                </>
              )}
            </button>
            {expanded && (courseOpen || isCourseManagementActive) && (
              <div className="ml-4 mt-1 space-y-0.5">
                {courseManagementSubItems.map(sub => (
                  permissions[sub.permission] && (
                    <Link
                      key={sub.id}
                      to={sub.path}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-all duration-150 ${location.pathname === sub.path
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-gray-600 hover:bg-white hover:shadow-sm'
                        }`}
                    >
                      <Icon icon="octicon:dot-16" width="16" className={location.pathname === sub.path ? 'text-primary' : 'text-gray-400'} />
                      {sub.text}
                    </Link>
                  )
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content Management */}
        {permissions['content_management_view'] && (!search || 'content management'.includes(search.toLowerCase())) && (
          <div>
            <button
              onClick={() => expanded && setContentOpen(!contentOpen)}
              title={!expanded ? 'Content Management' : undefined}
              className={`w-full flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-150 ${expanded ? 'px-3 py-2.5' : 'px-0 py-2.5 justify-center'
                } ${isContentActive
                  ? 'bg-surface text-font-primary shadow-sm'
                  : 'text-font-secondary hover:bg-white hover:shadow-sm'
                }`}
            >
              <Icon icon="fluent:content-view-24-regular" width="30" height="30" className={`shrink-0 ${isContentActive ? 'text-white bg-primary p-1.5 rounded-lg' : 'p-1.5 text-font-secondary'}`} />

              {expanded && (
                <>
                  <span className="flex-1 text-left">Content</span>
                  <Icon
                    icon={contentOpen || isContentActive ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                    width="16"
                    className={isContentActive ? 'text-white/70' : 'text-gray-400'}
                  />
                </>
              )}
            </button>
            {expanded && (contentOpen || isContentActive) && (
              <div className="ml-4  mt-1  space-y-0.5">
                {contentSubItems.map(sub =>
                  permissions[sub.permission] && (
                    <Link
                      key={sub.id}
                      to={sub.path}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-all duration-150 ${location.pathname === sub.path
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-gray-600 hover:bg-white hover:shadow-sm'
                        }`}
                    >
                      <Icon icon="octicon:dot-16" width="16" className={location.pathname === sub.path ? 'text-primary' : 'text-gray-400'} />
                      {sub.text}
                    </Link>
                  )
                )}
              </div>
            )}
          </div>
        )}

        {/* Support Management */}
        {permissions['support_management_view'] && (!search || 'support help'.includes(search.toLowerCase())) && (
          <div>
            <button
              onClick={() => expanded && setSupportOpen(!supportOpen)}
              title={!expanded ? 'Support & Help' : undefined}
              className={`w-full flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-150 ${expanded ? 'px-3 py-2.5' : 'px-0 py-2.5 justify-center'
                } ${isSupportActive
                  ? 'bg-surface text-font-primary shadow-sm'
                  : 'text-font-secondary hover:bg-white hover:shadow-sm'
                }`}
            >
              <Icon icon="fluent:person-support-24-regular" width="30" height="30" className={`shrink-0 ${isSupportActive ? 'text-white bg-primary p-1.5 rounded-lg' : 'p-1.5 text-font-secondary'}`} />
              {expanded && (
                <>
                  <span className="flex-1 text-left">Support & Help</span>
                  <Icon
                    icon={supportOpen || isSupportActive ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                    width="16"
                    className={isSupportActive ? 'text-white/70' : 'text-gray-400'}
                  />
                </>
              )}
            </button>
            {expanded && (supportOpen || isSupportActive) && (
              <div className="ml-4 mt-1 space-y-0.5">
                {supportSubItems.map(sub => (
                  permissions[sub.permission] && (
                    <Link
                      key={sub.id}
                      to={sub.path}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-all duration-150 ${location.pathname === sub.path
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-gray-600 hover:bg-white hover:shadow-sm'
                        }`}
                    >
                      <Icon icon="octicon:dot-16" width="16" className={location.pathname === sub.path ? 'text-primary' : 'text-gray-400'} />
                      {sub.text}
                    </Link>
                  )
                ))}
              </div>
            )}
          </div>
        )}
      </nav>



      {/* Logout */}
      <div className={`py-4 border-t border-gray-200 ${expanded ? 'px-3' : 'px-2'}`}>
        <button
          onClick={handleLogout}
          title={!expanded ? 'Logout' : undefined}
          className={`w-full flex items-center gap-3 rounded-xl text-sm font-medium text-red-500 hover:bg-white hover:shadow-sm transition-all duration-150 ${expanded ? 'px-3 py-2.5' : 'justify-center py-2.5'
            }`}
        >
          <Icon icon="fluent:sign-out-24-regular" width="19" className="shrink-0" />
          {expanded && 'Logout'}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:block fixed top-0 left-0 h-full z-40  transition-all duration-300 ${isExpanded ? 'w-64' : 'w-16'
          } ${isFloating ? 'shadow-2xl z-50' : ''}`}
        onMouseEnter={() => { if (collapsed) setHovered(true); }}
        onMouseLeave={() => setHovered(false)}
      >
        {navContent(isExpanded)}
      </aside>

      {/* Mobile: overlay drawer */}
      {open && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={onClose} />}
      <aside className={`fixed top-0 left-0 h-full w-64 z-50 flex flex-col border-r border-gray-200 transform transition-transform duration-300 lg:hidden ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        {navContent(true)}
      </aside>
    </>
  );
}

export default SideBar;
